/**
 * facilities.ts — WEB.4.UI.4
 *
 * Static data for the 6 primary buildable facilities.
 * Advanced 6 (biomass, floatingsolar, geothermal, offshoredrilling, tidal,
 * wastetoenergy) reserved for biome-specific specialty rows (future).
 *
 * Stats are illustrative placeholders matching the mockup. A future iteration
 * wires these to the rules-engine BuildCostService for live aura-modified values.
 *
 * Biome legality: conservative list. Only the rules-engine should be the
 * authoritative source; this list is the UI display gate only.
 */

export type FacilityCategory = "Fossil" | "Renewable" | "Nuclear" | "Geothermal" | "Waste";

export type FacilityDef = {
  /** Unique machine key — stored in builtFacilities[tileIdx] */
  id: string;
  /** Display name */
  name: string;
  category: FacilityCategory;
  /** Tagline shown in meta column */
  tagline: string;
  /** Icon path relative to public root (served from assets/ via vite publicDir) */
  iconSrc: string;
  /** Base build cost in tokens */
  baseCost: number;
  /** Energy produced per turn */
  energyPerTurn: number;
  /** Pollution produced per turn (positive = bad) */
  pollutionPerTurn: number;
  /** Toll opponents pay to land (tokens) */
  tollPerLand: number;
  /**
   * Tile types this facility CANNOT be built on.
   * If the current tile type is in this set, the row is shown as locked/illegal.
   */
  illegalOn: ReadonlySet<string>;
};

export const FACILITIES: ReadonlyArray<FacilityDef> = [
  {
    id: "coal",
    name: "Coal Plant",
    category: "Fossil",
    tagline: "High output · +3 pollution / turn",
    iconSrc: "/images/facilities/facility_coal.png",
    baseCost: 10,
    energyPerTurn: 8,
    pollutionPerTurn: 5,
    tollPerLand: 4,
    illegalOn: new Set(["River"]),
  },
  {
    id: "oil",
    name: "Oil Plant",
    category: "Fossil",
    tagline: "Steady output · +2 pollution / turn",
    iconSrc: "/images/facilities/facility_oil.png",
    baseCost: 12,
    energyPerTurn: 7,
    pollutionPerTurn: 4,
    tollPerLand: 3,
    illegalOn: new Set<string>(),
  },
  {
    id: "hydro",
    name: "Hydro Plant",
    category: "Renewable",
    tagline: "Requires water source",
    iconSrc: "/images/facilities/facility_hydro.png",
    baseCost: 15,
    energyPerTurn: 5,
    pollutionPerTurn: 0,
    tollPerLand: 2,
    // Hydro only legal on River, Coast, Mountain (water-adjacent). All others illegal.
    illegalOn: new Set(["Urban", "Plains", "Desert", "Tundra", "Forest", "Volcanic"]),
  },
  {
    id: "wind",
    name: "Wind Plant",
    category: "Renewable",
    tagline: "Clean · output varies by biome",
    iconSrc: "/images/facilities/facility_wind.png",
    baseCost: 14,
    energyPerTurn: 6,
    pollutionPerTurn: 0,
    tollPerLand: 3,
    illegalOn: new Set<string>(),
  },
  {
    id: "solar",
    name: "Solar Farm",
    category: "Renewable",
    tagline: "Excellent on Desert · no pollution",
    iconSrc: "/images/facilities/facility_solar.png",
    baseCost: 16,
    energyPerTurn: 4,
    pollutionPerTurn: 0,
    tollPerLand: 2,
    illegalOn: new Set(["Tundra"]),
  },
  {
    id: "nuclear",
    name: "Nuclear Plant",
    category: "Nuclear",
    tagline: "Massive output · meltdown risk on event draw",
    iconSrc: "/images/facilities/facility_nuclear.png",
    baseCost: 25,
    energyPerTurn: 12,
    pollutionPerTurn: 1,
    tollPerLand: 5,
    illegalOn: new Set(["Volcanic"]),
  },
  // ───────────────────────────────────────────────────────────────────────
  // WEB.4.UI.11b.2: advanced 6 (biomass / geothermal / waste / offshore-drilling
  // / floating-solar / tidal). Stats here are FALLBACK / DEFAULT — the BuildMenu
  // overrides with era-specific values from facility-rules.json. These stats
  // still feed legacy aura-build-cost / aura-tick lookups when no era context
  // is available (e.g. unit tests).
  // ───────────────────────────────────────────────────────────────────────
  {
    id: "biomass",
    name: "Biomass Plant",
    category: "Fossil",
    tagline: "Burns organic waste · moderate pollution",
    iconSrc: "/images/facilities/facility_biomass.png",
    baseCost: 10,
    energyPerTurn: 1,
    pollutionPerTurn: 5,
    tollPerLand: 2,
    illegalOn: new Set<string>(),
  },
  {
    id: "geothermal",
    name: "Geothermal Plant",
    category: "Geothermal",
    tagline: "Low-emission baseload · needs hot rock",
    iconSrc: "/images/facilities/facility_geothermal.png",
    baseCost: 20,
    energyPerTurn: 2,
    pollutionPerTurn: 3,
    tollPerLand: 4,
    illegalOn: new Set<string>(),
  },
  {
    id: "wastetoenergy",
    name: "Waste-to-Energy Plant",
    category: "Waste",
    tagline: "Burns refuse · urban baseload",
    iconSrc: "/images/facilities/facility_wastetoenergy.png",
    baseCost: 20,
    energyPerTurn: 2,
    pollutionPerTurn: 3,
    tollPerLand: 5,
    illegalOn: new Set<string>(),
  },
  {
    id: "offshoredrilling",
    name: "Offshore Drilling Platform",
    category: "Fossil",
    tagline: "Marine oil extraction · high pollution",
    iconSrc: "/images/facilities/facility_offshoredrilling.png",
    baseCost: 20,
    energyPerTurn: 4,
    pollutionPerTurn: 5,
    tollPerLand: 4,
    illegalOn: new Set<string>(),
  },
  {
    id: "floatingsolar",
    name: "Floating Solar Farm",
    category: "Renewable",
    tagline: "Solar on water · clean baseload",
    iconSrc: "/images/facilities/facility_floatingsolar.png",
    baseCost: 45,
    energyPerTurn: 2,
    pollutionPerTurn: 2,
    tollPerLand: 7,
    illegalOn: new Set<string>(),
  },
  {
    id: "tidal",
    name: "Tidal Power Plant",
    category: "Renewable",
    tagline: "Ocean turbines · sustainable era only",
    iconSrc: "/images/facilities/facility_tidal.png",
    baseCost: 25,
    energyPerTurn: 4,
    pollutionPerTurn: 2,
    tollPerLand: 9,
    illegalOn: new Set<string>(),
  },
] as const;

/** CSS category class suffix — maps category to mockup CSS token. */
export const CATEGORY_CSS: Record<FacilityCategory, string> = {
  Fossil: "catFossil",
  Renewable: "catRenewable",
  Nuclear: "catClean",
  Geothermal: "catGeothermal",
  Waste: "catWaste",
};

/**
 * WEB.4.UI.11b.2 — Map FacilityType (PascalCase, from facility-rules.json) to
 * the lowercase id stored in builtFacilities[tileIdx]. Used by BuildMenu when
 * calling buildFacility(facilityId, cost).
 */
export const FACILITY_TYPE_TO_ID: Record<string, string> = {
  Coal: "coal",
  Oil: "oil",
  Hydro: "hydro",
  Biomass: "biomass",
  Wind: "wind",
  Solar: "solar",
  Nuclear: "nuclear",
  Geothermal: "geothermal",
  WasteToEnergy: "wastetoenergy",
  OffshoreDrilling: "offshoredrilling",
  FloatingSolar: "floatingsolar",
  Tidal: "tidal",
};
